import { useEffect, useState } from "react";
import {
  updateProfile,
  getAccount,
  applyReferral,
  ApiError,
  type PublicUser,
  type StampInfo,
} from "./api.ts";
import { StampCard } from "./StampCard.tsx";
import { haptic } from "./telegram.ts";

/**
 * Optional customer account: nickname / email / phone, plus the stamp-card
 * summary and the shareable referral code. Never a gate to ordering — the
 * customer can skip it entirely.
 */
export function Account({
  user,
  onUpdated,
  onBack,
}: {
  user: PublicUser;
  onUpdated: (u: PublicUser) => void;
  onBack: () => void;
}) {
  const [nickname, setNickname] = useState(user.profile.nickname ?? "");
  const [email, setEmail] = useState(user.profile.email ?? "");
  const [phone, setPhone] = useState(user.profile.phone ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const [stamps, setStamps] = useState<StampInfo | null>(null);

  // Referral-code entry (invitee side) — only until the user is referred.
  const [refCode, setRefCode] = useState("");
  const [refBusy, setRefBusy] = useState(false);
  const [refMsg, setRefMsg] = useState<string | null>(null);
  const [refErr, setRefErr] = useState<string | null>(null);
  const redeemReferral = async () => {
    if (!refCode.trim()) return;
    setRefBusy(true);
    setRefErr(null);
    setRefMsg(null);
    try {
      const updated = await applyReferral(refCode.trim());
      onUpdated(updated);
      setRefMsg("Bonus applied — welcome! 🎉");
      haptic("success");
    } catch (e) {
      setRefErr(referralError(e));
      haptic("error");
    } finally {
      setRefBusy(false);
    }
  };

  useEffect(() => {
    getAccount()
      .then((r) => setStamps(r.stamps))
      .catch(() => {
        /* stamp card is optional */
      });
  }, []);

  const save = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const updated = await updateProfile({
        nickname: nickname.trim() || null,
        email: email.trim() || null,
        phone: phone.trim() || null,
      });
      onUpdated(updated);
      setSaved(true);
      haptic("medium");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "couldn’t save");
    } finally {
      setSaving(false);
    }
  };

  const copyReferral = () => {
    if (user.referralCode) {
      navigator.clipboard?.writeText(user.referralCode).catch(() => {});
      haptic("light");
    }
  };

  return (
    <>
      <div className="account-topbar">
        <button className="back-pill" onClick={onBack}>
          <span aria-hidden="true">←</span> Back to shop
        </button>
      </div>

      <section className="card">
        <h2 className="gate-title">Your account</h2>
        <p className="gate-text">
          Optional — add contact details for faster checkout and order updates.
        </p>

        <label className="field">
          <span className="field-label">Nickname</span>
          <input
            className="field-input"
            value={nickname}
            maxLength={40}
            placeholder="How should we call you?"
            onChange={(e) => setNickname(e.target.value)}
          />
        </label>

        <label className="field">
          <span className="field-label">Email</span>
          <input
            className="field-input"
            type="email"
            inputMode="email"
            value={email}
            placeholder="you@example.com"
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>

        <label className="field">
          <span className="field-label">Phone</span>
          <input
            className="field-input"
            type="tel"
            inputMode="tel"
            value={phone}
            placeholder="+31 6 …"
            onChange={(e) => setPhone(e.target.value)}
          />
        </label>

        {error && <p className="note error">{error}</p>}
        {saved && <p className="note">Saved ✓</p>}

        <button className="btn" onClick={save} disabled={saving}>
          {saving ? "Saving…" : "Save profile"}
        </button>
      </section>

      <section className="card">
        <div className="row row-strong">
          <span className="row-label">🎟️ Your stamp card</span>
        </div>
        <StampCard
          stamps={
            stamps ?? {
              count: user.stampCount,
              size: Math.max(user.stampCount + 1, 10),
              freeVapeCredits: user.freeVapeCredits,
              remaining: 0,
            }
          }
        />
        <p className="gate-text">
          {stamps
            ? `Collect ${stamps.size} stamps — one for every vape you buy — and your next vape is on us.`
            : "Collect a stamp for every vape you buy, and your next vape is on us."}
        </p>
      </section>

      {!user.referred && (
        <section className="card">
          <div className="row row-strong">
            <span className="row-label">🤝 Got a referral code?</span>
          </div>
          <p className="gate-text">
            Enter a friend’s code before your first order to claim a bonus stamp.
          </p>
          <div className="promo-row">
            <input
              className="field-input"
              value={refCode}
              placeholder="Friend’s code"
              autoCapitalize="characters"
              onChange={(e) => setRefCode(e.target.value.toUpperCase())}
            />
            <button
              className="btn secondary promo-apply"
              onClick={redeemReferral}
              disabled={refBusy || !refCode.trim()}
            >
              {refBusy ? "…" : "Apply"}
            </button>
          </div>
          {refMsg && <p className="note">{refMsg}</p>}
          {refErr && <p className="note error">{refErr}</p>}
        </section>
      )}

      {user.referralCode && (
        <section className="card">
          <div className="row row-strong">
            <span className="row-label">🎁 Invite friends</span>
          </div>
          <p className="gate-text">
            Share your code. Your friend gets a bonus stamp, and you earn one too
            when their first order is delivered.
          </p>
          <button className="referral-code" onClick={copyReferral}>
            {user.referralCode} <span className="copy-hint">tap to copy</span>
          </button>
        </section>
      )}

      <button className="btn account-done" onClick={onBack}>
        ← Back to shop
      </button>
    </>
  );
}

function referralError(e: unknown): string {
  const code = e instanceof ApiError ? e.message : "";
  switch (code) {
    case "not_found":
      return "That code doesn’t exist.";
    case "self_referral":
      return "You can’t use your own code.";
    case "already_referred":
      return "You’ve already used a referral code.";
    case "has_orders":
      return "Referral codes only work before your first order.";
    default:
      return "Couldn’t apply that code.";
  }
}
